import React, { useState, useEffect } from 'react';
import TeacherAssignmentsService from '../../services/TeacherAssignmentsService';
import { supabase } from '../../lib/supabase';
import './SubjectMembersView.css';

const SubjectMembersView = ({ tenantId, onBack }) => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectMembers, setSubjectMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [formData, setFormData] = useState({
    teacher_id: '',
    subject_name: '',
    teaching_level: '',
    responsibilities: '',
    notes: ''
  });

  useEffect(() => {
    console.log('SubjectMembersView mounted with tenantId:', tenantId);
    if (!tenantId) {
      setError('tenantId is required but not provided');
      return;
    }
    loadSubjects();
    loadTeachers();
  }, [tenantId]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all unique subject names from teacher_assignments
      const { data: assignments, error: assignmentsError } = await supabase
        .from('teacher_assignments')
        .select('subject_name')
        .eq('tenant_id', tenantId)
        .not('subject_name', 'is', null)
        .not('subject_name', 'eq', '');

      if (assignmentsError) {
        throw new Error(`Failed to fetch subjects: ${assignmentsError.message}`);
      }

      // Get unique subject names
      const uniqueSubjects = [...new Set(assignments.map(a => a.subject_name))].sort();
      setSubjects(uniqueSubjects.map(name => ({ name, displayName: name })));

    } catch (err) {
      console.error('Error loading subjects:', err);
      setError(`Failed to load subjects: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      setLoadingTeachers(true);
      const teachers = await TeacherAssignmentsService.getAllTeachers(tenantId);
      setAvailableTeachers(teachers);
    } catch (err) {
      console.error('Error loading teachers:', err);
      setError(`Failed to load teachers: ${err.message}`);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const handleSubjectSelect = async (subjectName) => {
    try {
      setLoading(true);
      setError('');
      setSelectedSubject(subjectName);

      console.log('Loading subject members for:', subjectName);

      // Get assignments for the specific subject
      const { data: assignments, error: assignmentsError } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('subject_name', subjectName);

      if (assignmentsError) {
        throw new Error(`Failed to fetch subject assignments: ${assignmentsError.message}`);
      }

      if (!assignments || assignments.length === 0) {
        setSubjectMembers([]);
        setMessage(`No teachers found for ${subjectName}`);
        return;
      }

      // Get teacher information for each assignment
      const teacherIds = [...new Set(assignments.map(a => a.teacher_id))];
      const { data: teachers, error: teachersError } = await supabase
        .from('saas_teachers')
        .select('id, name, code, english_name, department, teacher_no')
        .eq('tenant_id', tenantId)
        .in('id', teacherIds);

      if (teachersError) {
        throw new Error(`Failed to fetch teachers: ${teachersError.message}`);
      }

      // Combine assignments with teacher information
      const members = assignments.map(assignment => {
        const teacher = teachers.find(t => t.id === assignment.teacher_id);
        return {
          ...assignment,
          teacher_name: teacher ? teacher.name : 'Unknown Teacher',
          teacher_english_name: teacher ? teacher.english_name : '',
          teacher_department: teacher ? teacher.department : '',
          teacher_code: teacher ? teacher.code : '',
          teacher_no: teacher ? teacher.teacher_no : null
        };
      });

      setSubjectMembers(members);
      setMessage(`Loaded ${members.length} teachers for ${subjectName}`);

    } catch (err) {
      console.error('Error loading subject members:', err);
      setError(`Failed to load subject members: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    setFormData({
      teacher_id: '',
      subject_name: selectedSubject || '',
      teaching_level: '',
      responsibilities: '',
      notes: ''
    });
    setShowAddMemberModal(true);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setFormData({
      teacher_id: member.teacher_id || '',
      subject_name: member.subject_name || '',
      teaching_level: member.teaching_level || '',
      responsibilities: member.responsibilities?.join(', ') || '',
      notes: member.notes || ''
    });
    setShowEditMemberModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveMember = async () => {
    try {
      setLoading(true);
      setError('');

      if (!formData.teacher_id || !formData.subject_name) {
        setError('Teacher and Subject are required');
        return;
      }

      const selectedTeacher = availableTeachers.find(t => t.id === formData.teacher_id);
      const assignmentData = {
        tenant_id: tenantId,
        teacher_id: formData.teacher_id,
        assignment_type: 'subject_teacher',
        academic_year: '2025-26',
        term: '1',
        active_from: '2025-09-01',
        active_until: null,
        is_primary: false,
        responsibilities: formData.responsibilities ? formData.responsibilities.split(',').map(r => r.trim()).filter(r => r) : [],
        notes: formData.notes,
        class_id: null,
        subject_name: formData.subject_name,
        eca_id: null,
        committee_name: null,
        duty_name: null,
        duty_type: null,
        duty_location: null,
        teaching_level: formData.teaching_level,
        member_role: null,
        assignment_data: {
          source: 'manual_entry',
          teacher_code: selectedTeacher ? selectedTeacher.code : '',
          teacher_name: selectedTeacher ? selectedTeacher.name : 'Unknown Teacher',
          subject_name: formData.subject_name
        },
        metadata: {
          created_at: new Date().toISOString(),
          source: 'subject_members_view'
        }
      };

      if (selectedMember) {
        // Update existing member
        const { error } = await supabase
          .from('teacher_assignments')
          .update(assignmentData)
          .eq('id', selectedMember.id);

        if (error) {
          throw new Error(`Failed to update member: ${error.message}`);
        }

        setMessage(`Successfully updated teacher for ${formData.subject_name}`);
      } else {
        // Add new member
        const { error } = await supabase
          .from('teacher_assignments')
          .insert([assignmentData]);

        if (error) {
          throw new Error(`Failed to add member: ${error.message}`);
        }

        setMessage(`Successfully added teacher to ${formData.subject_name}`);
      }

      // Reload subject members
      if (selectedSubject) {
        await handleSubjectSelect(selectedSubject);
      }

      setShowAddMemberModal(false);
      setShowEditMemberModal(false);
      setSelectedMember(null);
      setFormData({
        teacher_id: '',
        subject_name: '',
        teaching_level: '',
        responsibilities: '',
        notes: ''
      });

    } catch (err) {
      setError(`Failed to save member: ${err.message}`);
      console.error('Error saving member:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this teacher assignment?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const { error } = await supabase
        .from('teacher_assignments')
        .delete()
        .eq('id', memberId);

      if (error) {
        throw new Error(`Failed to delete member: ${error.message}`);
      }

      setMessage('Successfully deleted teacher assignment');
      
      // Reload subject members
      if (selectedSubject) {
        await handleSubjectSelect(selectedSubject);
      }

    } catch (err) {
      setError(`Failed to delete member: ${err.message}`);
      console.error('Error deleting member:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddMemberModal(false);
    setShowEditMemberModal(false);
    setSelectedMember(null);
    setFormData({
      teacher_id: '',
      subject_name: '',
      teaching_level: '',
      responsibilities: '',
      notes: ''
    });
  };

  // Show error if tenantId is missing
  if (!tenantId) {
    return (
      <div className="subject-members-view">
        <div className="view-header">
          <h3>📚 Subject Members View</h3>
          <p>View and manage subject teachers</p>
          <button className="back-btn" onClick={onBack}>
            ← Back to Teachers
          </button>
        </div>
        <div className="error-message">
          ❌ tenantId is required but not provided. Please check the configuration.
        </div>
      </div>
    );
  }

  return (
    <div className="subject-members-view">
      <div className="view-header">
        <h3>📚 Subject Members View</h3>
        <p>View and manage subject teachers</p>
        <button className="back-btn" onClick={onBack}>
          ← Back to Teachers
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {message && (
        <div className="success-message">
          ✅ {message}
        </div>
      )}

      <div className="view-content">
        {/* Subjects List */}
        <div className="subjects-list">
          <h4>📖 Available Subjects ({subjects.length})</h4>
          <div className="subjects-grid">
            {subjects.map((subject) => (
              <div
                key={subject.name}
                className={`subject-card ${selectedSubject === subject.name ? 'selected' : ''}`}
                onClick={() => handleSubjectSelect(subject.name)}
              >
                <div className="subject-info">
                  <h5>{subject.displayName}</h5>
                  <p>Click to view teachers</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Subject Members Details */}
        {selectedSubject && (
          <div className="subject-members-details">
            <div className="subject-header">
              <h4>📖 {selectedSubject}</h4>
              <p>Teachers assigned to this subject</p>
              <button className="add-member-btn" onClick={handleAddMember}>
                + Add Teacher
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading teachers...</div>
            ) : subjectMembers && subjectMembers.length > 0 ? (
              <div className="subject-members-section">
                <h5>Subject Teachers ({subjectMembers.length})</h5>
                <div className="subject-members-list">
                  {subjectMembers.map((member, index) => (
                    <div key={index} className="subject-member-item">
                      <div className="member-info">
                        <div className="member-details">
                          <h6>{member.teacher_name || 'Unknown Teacher'}</h6>
                          {member.teacher_english_name && (
                            <p className="member-english">{member.teacher_english_name}</p>
                          )}
                          {member.teacher_department && (
                            <p className="member-dept">{member.teacher_department}</p>
                          )}
                        </div>
                        <div className="member-role-info">
                          {member.teaching_level && (
                            <span className="level-badge">{member.teaching_level}</span>
                          )}
                          <span className="subject-badge">{member.subject_name}</span>
                        </div>
                      </div>
                      <div className="member-actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditMember(member)}
                          title="Edit Teacher"
                        >
                          ✏️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteMember(member.id)}
                          title="Delete Teacher"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-members">
                <p>No teachers found for this subject.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Member Modal */}
      {(showAddMemberModal || showEditMemberModal) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{selectedMember ? 'Edit Subject Teacher' : 'Add Subject Teacher'}</h3>
              <button 
                className="close-btn" 
                onClick={handleCancel}
                disabled={loading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Teacher:</label>
                {loadingTeachers ? (
                  <div className="loading-teachers">Loading teachers...</div>
                ) : availableTeachers.length > 0 ? (
                  <select
                    value={formData.teacher_id}
                    onChange={(e) => handleFormChange('teacher_id', e.target.value)}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value="">Select a teacher...</option>
                    {availableTeachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} {teacher.english_name ? `(${teacher.english_name})` : ''} - {teacher.department || 'No Department'}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="no-teachers">
                    <span>No teachers available</span>
                    <button onClick={loadTeachers} className="retry-btn" disabled={loadingTeachers}>Retry</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Subject Name:</label>
                <input
                  type="text"
                  value={formData.subject_name}
                  onChange={(e) => handleFormChange('subject_name', e.target.value)}
                  className="form-input"
                  placeholder="Enter subject name"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Teaching Level:</label>
                <select
                  value={formData.teaching_level}
                  onChange={(e) => handleFormChange('teaching_level', e.target.value)}
                  className="form-input"
                  disabled={loading}
                >
                  <option value="">Select teaching level...</option>
                  <option value="Junior">Junior</option>
                  <option value="Senior">Senior</option>
                  <option value="All">All Levels</option>
                </select>
              </div>
              <div className="form-group">
                <label>Responsibilities:</label>
                <input
                  type="text"
                  value={formData.responsibilities}
                  onChange={(e) => handleFormChange('responsibilities', e.target.value)}
                  className="form-input"
                  placeholder="Enter responsibilities (comma-separated)"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Notes:</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  className="form-input"
                  placeholder="Enter additional notes"
                  rows="3"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={handleSaveMember}
                disabled={loading || !formData.teacher_id || !formData.subject_name}
              >
                {loading ? 'Saving...' : (selectedMember ? 'Update Teacher' : 'Add Teacher')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectMembersView;
